/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo, useContext, useEffect, useState } from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import TrendChart from '../../../shared/TrendChart/TrendChart';
import { convertStackedDataForCarbonTrendChart } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ICarbonScopeOneStackedData } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import { getCarbonTotalAggregationValues } from '../../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import StackedColumnChart from '../../../shared/StackedColumnChart/StackedColumnChart';
import { ApiContext } from '../../../../context/ApiContext';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';

interface ICarbonScopeOneStackedDetailsComponentProps {
  data: ICarbonScopeOneStackedData;
  trendChartCategory: string;
  carbonAllSiteMonthData?: boolean;
}
const CarbonScopeOneStackedDetailsComponent = (
  props: ICarbonScopeOneStackedDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t, lng] = useTranslation();
  const apiContext = useContext(ApiContext);
  const { data, carbonAllSiteMonthData } = props;
  const {
    emissionMeasurementUnit,
    totalAggregations,
    monthWiseEmissionByFuelType
  } = data;
  const {
    totalActualEmission,
    totalPreviousPeriodEmission,
    totalDeviation,
    totalDeviationPercentage
  } = totalAggregations;

  const valuesArrayForSummary = [
    totalActualEmission,
    totalPreviousPeriodEmission,
    totalDeviation
  ];

  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    emissionMeasurementUnit,
    valuesArrayForSummary
  );
  const getScaledValue = (value: number) => {
    return getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      value,
      maxValueUnitConversion.Unit
    ).Value;
  };

  const aggregationSummaryData = {
    actualCost: getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      totalActualEmission,
      getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        [totalActualEmission]
      ).Unit
    ).Value,
    actualUnit: getMaxConvertedValueFromArray(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      [totalActualEmission]
    ).Unit,
    baselineCost: getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      totalPreviousPeriodEmission,
      getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        [totalPreviousPeriodEmission]
      ).Unit
    ).Value,
    baselineUnit: getMaxConvertedValueFromArray(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      [totalPreviousPeriodEmission]
    ).Unit,
    costSavings:
      (apiContext.showChangeWhenData &&
        (isNaN(Number(totalPreviousPeriodEmission)) ||
          isNaN(Number(totalActualEmission)) ||
          Number(totalPreviousPeriodEmission) === 0 ||
          Number(totalActualEmission) === 0 ||
          Math.abs(Number(totalDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD)) ||
      !carbonAllSiteMonthData
        ? null
        : getConvertedValue(
            ConversionType.CARBON,
            emissionMeasurementUnit,
            totalDeviation,
            getMaxConvertedValueFromArray(
              ConversionType.CARBON,
              emissionMeasurementUnit,
              [totalDeviation]
            ).Unit
          ).Value,
    changeUnit: getMaxConvertedValueFromArray(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      [totalDeviation]
    ).Unit,
    costSavingsPercentage: parseFloat(
      totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    )
  };
  const [chartData, setChartData] = useState(
    convertStackedDataForCarbonTrendChart(
      monthWiseEmissionByFuelType,
      emissionMeasurementUnit,
      undefined,
      lng
    )
  );
  const [state] = useFilterScope1Context();

  useEffect(() => {
    if (state?.saveFilterData?.length > 0) {
      const selectedStacks = state.saveFilterData.map(
        (fuel: { boolVal: boolean; childName: string }) => {
          if (fuel.boolVal === true) return fuel.childName;
        }
      );
      let allChartData = convertStackedDataForCarbonTrendChart(
        monthWiseEmissionByFuelType,
        emissionMeasurementUnit,
        undefined,
        lng
      );
      const filteredStacks = allChartData.fuel.filter((stack) => {
        if (selectedStacks.includes(stack.name))
          return { ...stack, selected: true };
      });
      setChartData({
        ...chartData,
        fuel: filteredStacks
      });
    }
  }, [state]);

  return (
    <div className="scopeOneStackedColumnChart">
      <DetailSummaryComponent
        aggregationData={aggregationSummaryData}
        measurementUnit={maxValueUnitConversion.Unit}
        detailType={t(e.SCOPEONEKEY)}
        renderedFromCarbon={true}
        fromCarbonScopeOne={true}
      />

      <StackedColumnChart
        unit={chartData.unit}
        chartData={chartData}
        isAllDataAvailable={false}
        renderedFromCarbon={true}
      />
    </div>
  );
};

export default memo(CarbonScopeOneStackedDetailsComponent);
